// Pure helpers for the News editor (no React, no network) so every rule is unit-testable.
//
// Why they exist: the API sanitizes descriptions server-side (sanitize-html in
// newsSanitize.js) and answers 200 even when it strips content, and CKEditor 5
// silently reshapes HTML it does not model. Posts published with curl (the
// 2026-09-29 events) must not be damaged by simply opening them in this form.

// Mirrors the API's <img> allowlist (app/utils/newsSanitize.js). An <img> from
// anywhere else is dropped by the server on save, so the editor warns first.
export const ALLOWED_IMAGE_PREFIX =
  process.env.REACT_APP_NEWS_IMAGE_PREFIX ||
  "https://rootsfundingstoragedev.blob.core.windows.net/";

const BLOCK_TAGS = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "LI", "FIGURE", "FIGCAPTION",
  "BLOCKQUOTE", "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "DIV", "HR",
  "PRE", "SECTION", "ARTICLE", "IMG",
]);
// Containers whose whitespace-only text children never render.
const WHITESPACE_CONTAINERS = new Set([
  "BODY", "UL", "OL", "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "FIGURE", "BLOCKQUOTE", "DIV",
]);

function parse(html) {
  const source = String(html == null ? "" : html).replace(/\r\n?/g, "\n");
  return new DOMParser().parseFromString(`<body>${source}</body>`, "text/html").body;
}

function isBlockElement(node) {
  return !!node && node.nodeType === 1 && BLOCK_TAGS.has(node.nodeName);
}

function canonicalize(node) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) {
      const insignificant =
        /^\s*$/.test(child.nodeValue) &&
        (WHITESPACE_CONTAINERS.has(node.nodeName) ||
          isBlockElement(child.previousSibling) ||
          isBlockElement(child.nextSibling));
      if (insignificant && node.nodeName !== "PRE") node.removeChild(child);
    } else if (child.nodeType === 1) {
      // Attribute order is not meaningful; sort so two serializers compare equal.
      const attrs = Array.from(child.attributes).map((a) => [a.name, a.value]);
      attrs.forEach(([name]) => child.removeAttribute(name));
      attrs.sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, value]) => child.setAttribute(name, value));
      canonicalize(child);
    } else {
      node.removeChild(child); // comments etc. never survive the server sanitizer
    }
  }
}

/**
 * canonicalHtml(html): one serialization for semantically identical HTML.
 * CRLF and LF are equal, sanitize-html's `<br />` equals CKEditor's `<br>`,
 * whitespace between block elements and attribute order are ignored. Anything
 * that changes what renders (a dropped element, attribute or inline space)
 * still differs.
 */
export function canonicalHtml(html) {
  const body = parse(html);
  canonicalize(body);
  return body.innerHTML.trim();
}

/** True when the editor's output renders the same document as the stored HTML. */
export function roundTrips(storedHtml, editorHtml) {
  return canonicalHtml(storedHtml) === canonicalHtml(editorHtml);
}

export function imageSources(html) {
  return Array.from(parse(html).querySelectorAll("img")).map((img) => img.getAttribute("src") || "");
}

export function countImages(html) {
  return imageSources(html).length;
}

function isHttpUrl(value) {
  try {
    const parsed = new URL(String(value).trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (_) {
    return false;
  }
}

/**
 * lintDescription: warnings shown before saving. Each one describes something
 * the server or the public site would do to the post that the author probably
 * does not expect. Warnings never block; the author confirms.
 */
export function lintDescription({ html, categories = [], eventDate = "", eventUrl = "" }) {
  const warnings = [];
  const raw = String(html || "");
  const body = parse(raw);

  const offHost = imageSources(raw).filter((src) => !src.startsWith(ALLOWED_IMAGE_PREFIX));
  if (offHost.length) {
    warnings.push({
      code: "OFF_HOST_IMAGE",
      message: `${offHost.length} image(s) are not uploaded through this form (pasted or linked from another site). The server removes them when you save. Use "Upload image" or "Add gallery images" instead.`,
    });
  }
  if (/hsforms\.com|2tfEd/i.test(raw) || /investor\s+club/i.test(body.textContent || "")) {
    warnings.push({
      code: "HUBSPOT_BUTTON",
      message: 'The text mentions the Investor Club or links a HubSpot form. The public page replaces that with a "Join the Roots Investor Club" button. Remove it if that is not what you want.',
    });
  }
  if (/<(oembed|iframe|script|object|embed|video|audio|svg|canvas)\b/i.test(raw)) {
    warnings.push({
      code: "EMBED_REMOVED",
      message: "Embedded media, iframes and scripts are removed when you save. To show a YouTube video, paste the YouTube link as normal text; the site turns it into a player.",
    });
  }
  if (/<[a-z][^>]*\sstyle\s*=/i.test(raw)) {
    warnings.push({
      code: "STYLE_REMOVED",
      message: "Inline styles (text alignment, colours, font sizes) are removed when you save. Use headings, bold, lists and links for structure instead.",
    });
  }
  if (categories.some((name) => /event/i.test(String(name))) && !String(eventDate || "").trim()) {
    warnings.push({
      code: "EVENT_WITHOUT_DATE",
      message: "This is an Events post without an event date. The site then shows the publishing date and cannot tell whether the event is upcoming or past.",
    });
  }
  if (String(eventUrl || "").trim() && !isHttpUrl(eventUrl)) {
    warnings.push({
      code: "BAD_EVENT_URL",
      message: "The event URL must start with http:// or https:// (for example https://luma.com/abc123).",
    });
  }
  return warnings;
}

function escapeAttr(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** True when the post already has an "Event Gallery" heading or paragraph. */
export function hasGalleryHeading(html) {
  return Array.from(parse(html).querySelectorAll("h1,h2,h3,h4,h5,h6,p")).some((el) =>
    /event gallery/i.test(el.textContent || "")
  );
}

/**
 * galleryHtml(items, { withHeading }): uploaded images as figures, one after
 * another. The public site shows images placed in a row as one slideshow, in
 * that place (portal buildArticleSegments). The "Event Gallery" heading is
 * added only when asked, so a post gets it once.
 */
export function galleryHtml(items, { withHeading = false } = {}) {
  for (const item of items) {
    if (!String(item.url || "").startsWith(ALLOWED_IMAGE_PREFIX)) {
      throw new Error(`${item.url} is not an uploaded image`);
    }
  }
  const figures = items
    .map((item) => `<figure class="image"><img src="${escapeAttr(item.url)}" alt="${escapeAttr(item.alt)}"></figure>`)
    .join("");
  return `${withHeading ? "<h4>Event Gallery</h4>" : ""}${figures}`;
}

/**
 * appendGallery(html, items): append uploaded images as a gallery at the end
 * of the post, under an "Event Gallery" heading unless the post has one.
 */
export function appendGallery(html, items) {
  return `${String(html || "")}${galleryHtml(items, { withHeading: !hasGalleryHeading(html) })}`;
}

/**
 * galleryAlt(title, existingImages, index): the alt text of the index-th new
 * gallery image, numbered on from the images already in the post. Numbering
 * per upload batch gave a post eight images all called "gallery image 1"
 * (dev post 89).
 */
export function galleryAlt(title, existingImages, index) {
  return `${String(title || "").trim() || "Event"} gallery image ${existingImages + index + 1}`;
}

// Text plus element structure, after the same canonicalization the round-trip
// guard uses: CRLF, <br />, attribute order and block whitespace do not count,
// a dropped element or a changed word does.
function structureSignature(html) {
  const body = parse(html);
  canonicalize(body);
  const tags = Array.from(body.querySelectorAll("*")).map((el) => el.nodeName).join(",");
  const text = (body.textContent || "").replace(/\s+/g, " ").trim();
  return `${tags}|${text}`;
}

const blankToNull = (v) => (v === undefined || v === null || String(v).trim() === "" ? null : String(v).trim());

/**
 * describeReadBackLoss(sent, stored): compare what the form sent with the row
 * the API stored. The API answers 200 even when its sanitizer strips images,
 * so the editor reads the row back and tells the author what did not survive.
 */
export function describeReadBackLoss(sent, stored) {
  const storedImages = new Set(imageSources(stored.description));
  const removedImages = imageSources(sent.description).filter((src) => !storedImages.has(src));
  const changedFields = [];
  if (blankToNull(sent.event_url) !== blankToNull(stored.event_url)) changedFields.push("event_url");
  const day = (v) => (blankToNull(v) ? String(v).slice(0, 10) : null);
  if (day(sent.event_date) !== day(stored.event_date)) changedFields.push("event_date");
  if (blankToNull(sent.title) !== blankToNull(stored.title)) changedFields.push("title");
  const contentChanged = structureSignature(sent.description) !== structureSignature(stored.description);
  return {
    removedImages,
    changedFields,
    contentChanged,
    any: removedImages.length > 0 || changedFields.length > 0 || contentChanged,
  };
}
